import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link2, Link2Off, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Settings → WordPress connection → "WordPress.com (Jetpack stats)".
 *
 * Same shape as SocialConnectionsSection: click Connect, get bounced to
 * WordPress.com, land back on /settings with `?wpcom_status=...` which
 * we flash as a toast and then strip from the URL so a refresh doesn't
 * re-fire it.
 *
 * The token this grants is what fills the amber website-views pill in
 * the newsletter picker. It is stored server-side on the property doc
 * and never sent back to the browser — `wpcom_status` carries only
 * booleans and the blog URL.
 *
 * Worth knowing when this fails: WordPress.com matches the redirect URI
 * against the app registration character for character, so the connect
 * flow only completes on the production host. On an Emergent preview
 * URL it comes back as a redirect_uri_mismatch, which the backend
 * translates into a readable message rather than a bare error code.
 */
export default function WpcomConnectionSection() {
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();

  const { data: status, isLoading } = useQuery({
    queryKey: ["wpcom-status"],
    queryFn: api.wpcomStatus,
    refetchOnMount: true,
  });

  useEffect(() => {
    const s = sp.get("wpcom_status");
    if (!s) return;
    const msg = sp.get("wpcom_msg") || "";
    if (s === "connected") {
      toast.success(`WordPress.com connected${msg ? ` — ${msg}` : ""}`);
      qc.invalidateQueries({ queryKey: ["wpcom-status"] });
      qc.invalidateQueries({ queryKey: ["newsletter-candidates"] });
    } else if (s === "denied") {
      toast.warning(`You denied the WordPress.com connection (${msg})`);
    } else {
      toast.error(`WordPress.com connection failed — ${msg}`);
    }
    const clean = new URLSearchParams(sp);
    clean.delete("wpcom_status");
    clean.delete("wpcom_msg");
    setSp(clean, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = useMutation({
    mutationFn: api.wpcomAuthUrl,
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (e) =>
      toast.error(
        e?.response?.data?.detail ||
          e?.message ||
          "Could not start the WordPress.com connection",
      ),
  });

  const disconnect = useMutation({
    mutationFn: api.wpcomDisconnect,
    onSuccess: () => {
      toast.success("WordPress.com disconnected");
      qc.invalidateQueries({ queryKey: ["wpcom-status"] });
    },
    onError: (e) =>
      toast.error(e?.response?.data?.detail || "Could not disconnect"),
  });

  const connected = !!status?.connected;
  const appReady = !!status?.app_configured;

  return (
    <div className="pt-4 mt-4 border-t border-[var(--border)]">
      <h3 className="font-mono-display text-[11px] uppercase tracking-wider text-[var(--ink-muted)] mb-2">
        WordPress.com (Jetpack stats)
      </h3>
      <p className="text-xs text-[var(--ink-muted)] mb-3">
        Connect this property's WordPress.com account to read Jetpack view
        counts. This is what fills the website-views pill in the newsletter
        picker. Read-only — it never publishes anything.
      </p>

      {isLoading ? (
        <div className="text-xs text-[var(--ink-muted)] flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking…
        </div>
      ) : (
        <>
          {connected && (
            <div
              data-testid="wpcom-connected"
              className="flex items-center gap-2 text-xs text-emerald-800 bg-emerald-50 border border-emerald-200 px-2.5 py-1.5 mb-3"
            >
              <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">
                Connected{status?.blog_url ? ` — ${status.blog_url}` : ""}
                {status?.blog_id ? ` (blog ${status.blog_id})` : ""}
              </span>
            </div>
          )}
          {!appReady && (
            <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-1.5 mb-3">
              WordPress.com app credentials are not set on the server yet.
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              data-testid="wpcom-connect"
              onClick={() => connect.mutate()}
              disabled={!appReady || connect.isPending}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs bg-[var(--slate-900)] text-white hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {connect.isPending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Link2 className="w-3.5 h-3.5" />
              )}
              {connected ? "Reconnect" : "Connect WordPress.com"}
            </button>
            {connected && (
              <button
                type="button"
                data-testid="wpcom-disconnect"
                onClick={() => disconnect.mutate()}
                disabled={disconnect.isPending}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-[var(--border-strong)] hover:bg-[var(--secondary)] disabled:opacity-50"
              >
                {disconnect.isPending ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Link2Off className="w-3.5 h-3.5" />
                )}
                Disconnect
              </button>
            )}
          </div>
          {status?.redirect_uri && (
            <p className="text-[11px] text-[var(--ink-muted)] mt-2 font-mono-display break-all">
              Redirect URI: {status.redirect_uri}
            </p>
          )}
        </>
      )}
    </div>
  );
}